import { z } from 'zod';
import { apiFor } from '@/api/client';

/*
 * Runtime contract for the "Le Grand Jeu Monopoly — Carrefour France" realtime
 * dashboard payload.
 *
 * Transcribed from the design handoff (Monopoly 2026 Realtime Dashboard.dc.html):
 * a 5-week campaign (08 Sep – 12 Oct 2026) with five tabs — Overview,
 * Participants & opt-in, Visits & sessions, Cards, Collections & prizes.
 *
 * THIS SCHEMA IS THE STEP-2 CONTRACT: the pipeline must emit exactly this
 * shape. Notes for whoever implements it:
 *   - `days` are ISO dates for the PERIOD shown (the design shows 08–19 Sep);
 *     every `*ByDay` array aligns index-for-index with it.
 *   - Counts stay numeric (the UI formats them en-US, as the design does);
 *     percentages are numbers 0–100; durations the backend can't express
 *     numerically are PRE-FORMATTED ("4m 12s"), the AH convention.
 *   - The same figure appears on several tabs in the design (visits, opt-ins…).
 *     It is carried ONCE here and the overview reads the tab sections, so two
 *     tiles can never disagree.
 *   - "Realtime" = the UI polls this payload; the pipeline's cadence is the
 *     freshness floor. `meta.last_refreshed_at` drives the "updated" stamp.
 */

const campaignSchema = z.object({
  name: z.string(),
  /** Header subtitle ("Realtime campaign dashboard · 5-week campaign · 08 Sep – 12 Oct 2026"). */
  subtitle: z.string(),
  /** Period selector, display strings ("08/09/2026", "19/09/2026"). */
  periodStart: z.string(),
  periodEnd: z.string(),
  currentDay: z.number(),
  totalDays: z.number(),
  /** True while the campaign is running — drives the LIVE pill. */
  live: z.boolean().optional(),
});

/** A labelled bar with an absolute value and its share (HBar rows, funnels). */
const shareRowSchema = z.object({ label: z.string(), value: z.number(), pct: z.number() });

const overviewSchema = z.object({
  /** Hero: codes redeemed across all channels. */
  cardsUsed: z.object({
    total: z.number(),
    perDay: z.number(),
    printed: z.number(),
    physical: z.number(),
    digital: z.number(),
  }),
  cardsUsedByDay: z.array(z.number()),
  /** Participants who joined TODAY (hero KPI "▲ 69,204 today"). */
  participantsToday: z.number(),
  cardsPerParticipant: z.number(),
  /** Cards issued vs. opened. */
  cardsIssued: z.object({
    /** `total` = physical print run; OPTIONAL until the split of the run is confirmed. */
    physical: z.object({ used: z.number(), total: z.number().optional() }),
    digital: z.object({ opened: z.number(), granted: z.number() }),
    codesRejectedPct: z.number(),
  }),
  /**
   * Roll a dice & board laps. OPTIONAL: the pipeline publishes it only once the
   * dice ledger has recorded consumed throws; laps have no source yet.
   */
  /**
   * Roll a dice & board laps. OPTIONAL as a group (needs the board promotion).
   * `throwsEarned` and the board counters are exact; `rolled` appears only when
   * a throw being USED is recorded somewhere, which the balance does not do.
   */
  dice: z
    .object({
      throwsEarned: z.number(),
      playersRolled: z.number(),
      /** Steps rolled on the board — the sum of the two dice per roll, not a roll count. */
      steps: z.number().optional(),
      boardsCompleted: z.number().optional(),
      avgStepsPerPlayer: z.number().optional(),
      lapsPerPlayer: z.number().optional(),
      rolled: z.number().optional(),
      avgRollsPerPlayer: z.number().optional(),
    })
    .optional(),
});

const participantsSchema = z.object({
  unique: z.number(),
  optIns: z.number(),
  marketingConsents: z.number(),
  returningPct: z.number(),
  newByDay: z.array(z.number()),
  /** Onboarding funnel, top to bottom; pct is share of the first step. */
  funnel: z.array(shareRowSchema),
  /**
   * Consents ACCORDED, summing every type below. Not the same as
   * `marketingConsents`, which counts members: one member accepting three
   * channels is 1 there and 3 here. OPTIONAL: absent before 2026-09-11.
   */
  consentsGranted: z.number().optional(),
  /** Consents collected per day, aligned to `days`. OPTIONAL: absent before 2026-09-14. */
  consentsByDay: z.array(z.number()).optional(),
  /** Every consent type the tenant records; pct is bar width relative to the largest. */
  consentChannels: z.array(shareRowSchema),
});

const visitsSchema = z.object({
  total: z.number(),
  uniqueVisitors: z.number(),
  sessionsPerVisitor: z.number(),
  /** Pre-formatted ("4m 12s"). OPTIONAL: needs the analytics events stream. */
  avgSession: z.string().optional(),
  byDay: z.array(z.number()),
  /** Where visits come from — shares sum to ~100. OPTIONAL: no source measures it yet. */
  sources: z.array(z.object({ label: z.string(), pct: z.number() })).optional(),
  /** OPTIONAL: needs the analytics events stream. */
  topScreens: z.array(z.object({ label: z.string(), value: z.number() })).optional(),
});

const cardsSchema = z.object({
  printed: z.number(),
  /** OPTIONAL: the physical/digital split of the print run, once confirmed. */
  printedPhysical: z.number().optional(),
  printedDigital: z.number().optional(),
  digitalGranted: z.number(),
  avgPacksPerOrder: z.number(),
  codeErrors: z.number(),
  usersBlocked: z.number(),
  /** Per day, aligned to `days`: physical codes vs. digital packs opened. */
  byDay: z.array(z.object({ physical: z.number(), digital: z.number() })),
  /** Per day: dice throws and completed board laps. OPTIONAL: no per-day dice source yet. */
  diceByDay: z.array(z.object({ rolled: z.number(), laps: z.number() })).optional(),
  /**
   * Street cards among the cards redeemed — the collectible half of the board.
   * `claimed` counts cards handed out (duplicates included), `distinct` how many
   * of the board's `total` streets have been seen at all, `rare` the gating
   * cards. OPTIONAL: a payload written before 2026-09-11 has none of it.
   */
  streets: z.object({ claimed: z.number(), distinct: z.number(), total: z.number(), rare: z.number() }).optional(),
  /** Digital packs by e-commerce channel. */
  channels: z.array(
    z.object({
      label: z.string(),
      minOrder: z.string(),
      packs: z.number(),
      sharePct: z.number(),
      /** OPTIONAL: orders are not attributed to a channel in the source. */
      perOrder: z.number().optional(),
    }),
  ),
});

const prizesSchema = z.object({
  instantWins: z.number(),
  instantWinsTotal: z.number(),
  collectionsCompleted: z.number(),
  collectionGroups: z.number(),
  chanceCards: z.number(),
  /** Free-form note under the chance-cards KPI ("12 daily draws paid · €1,000 each"). */
  chanceNote: z.string(),
  /** Street collections — prizes won vs. stock. `color` is the board colour swatch. */
  collections: z.array(
    z.object({
      color: z.string(),
      name: z.string(),
      prize: z.string(),
      stock: z.number(),
      won: z.number(),
      /**
       * The scarce card that gates the collection, and how many have been
       * claimed. OPTIONAL: a payload written before 2026-09-10 has neither.
       */
      rare: z.string().optional(),
      rareClaimed: z.number().optional(),
    }),
  ),
  /** Instant-win mix; pct is share of instant wins. */
  instantWinMix: z.array(shareRowSchema),
});

export const monopolyPayloadSchema = z.object({
  campaign: campaignSchema,
  /** ISO dates of the period shown; every *ByDay series aligns to it. */
  days: z.array(z.string()),
  overview: overviewSchema,
  participants: participantsSchema,
  visits: visitsSchema,
  cards: cardsSchema,
  prizes: prizesSchema,
});

export const monopolyEnvelopeSchema = z.object({
  data: monopolyPayloadSchema,
  meta: z.object({ last_refreshed_at: z.string().nullable() }).partial().optional(),
});

export type MonopolyPayload = z.infer<typeof monopolyPayloadSchema>;
export type MonopolyEnvelope = z.infer<typeof monopolyEnvelopeSchema>;
export type ShareRow = z.infer<typeof shareRowSchema>;

/** Same generic report endpoint every dashboard uses: GET <base>/reports/<id>. */
export async function getMonopolyReport(reportId: string, baseUrl: string): Promise<MonopolyEnvelope> {
  const raw = await apiFor(baseUrl).get(`reports/${reportId}`).json();
  return monopolyEnvelopeSchema.parse(raw);
}
